<script setup lang="ts">
import {
  ref, reactive, computed, watch,
} from 'vue';
import { storeToRefs, StoreGeneric } from 'pinia';
import Steps from '@/interfaces/Steps';
import ConnxProgressSteps from '@/components/Molecules/ConnxProgressSteps.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxAlert from '@/components/Molecules/ConnxAlert.vue';
import useEmployeeManagementActions from '@/stores/Employee/employee-management-actions';
import useApplicationStore from '@/stores/application';
import ConnxForm from '@/components/Molecules/Field/ConnxForm.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';

const employeeManagementActions = useEmployeeManagementActions();
const applicationStore = useApplicationStore();
const newEmpCode = ref('');
const { employee } = storeToRefs<StoreGeneric>(employeeManagementActions);
const emit = defineEmits(['close']);

const confirmEmployeeChange = ref(false);
const errorMessage = ref('');
const empCodeExists = ref(false);
const newEmpCodeField = ref();
const currentStep = ref(1);
const completed = ref(false);
const steps: Steps[] = reactive([
  {
    title: 'Set New Employee Code',
  },
  {
    title: 'Confirm Change',
  },
  {
    icon: 'fas fa-check',
  },
]);

const setCompleted = () => {
  currentStep.value = steps.length;
  completed.value = true;
  steps[2].title = 'Success!';
  steps[2].cssTextOverrides = 'tw-text-connx-success';
};

const validateAndIncrementStep = async () => {
  if (currentStep.value === 1) {
    newEmpCode.value = newEmpCode.value.trim();
    empCodeExists.value = await employeeManagementActions.validateEmployeeCodeExists(newEmpCode.value);
    if (!empCodeExists.value) {
      currentStep.value += 1;
    }
  } else if (currentStep.value === 2) {
    errorMessage.value = '';
    applicationStore.setLoading(true);
    const response = await employeeManagementActions.changeEmpCode(newEmpCode.value);
    applicationStore.setLoading(false);
    if (response?.error) {
      // Error
      steps[2].cssOverrides = '!tw-bg-connx-danger-shade-10';
      steps[2].icon = 'far fa-times';
      steps[2].title = 'Error Encountered!';
      steps[2].cssTextOverrides = '!tw-text-connx-danger-shade-15';

      // eslint-disable-next-line prefer-destructuring
      errorMessage.value = response.message;
      currentStep.value += 1;
    } else {
      // Success
      setCompleted();
    }
  }
};

const decrementStep = () => {
  if (currentStep.value > 0) {
    if (steps[2].icon !== 'fas fa-check') {
      steps[2].cssOverrides = 'tw-bg-connx-danger-shade-10';
      steps[2].icon = 'fas fa-check';
      steps[2].title = '';
      steps[2].cssTextOverrides = 'tw-text-connx-success';
    }
    currentStep.value -= 1;
  }
};

const nextButtonDisabled = computed(() => {
  switch (currentStep.value) {
    case (1):
      return newEmpCode.value === '' || empCodeExists.value;
    case (2):
      return !confirmEmployeeChange.value;
    default:
      return true;
  }
});

watch(newEmpCode, () => {
  empCodeExists.value = false;
});
</script>

<template>
  <div class="tw-fixed tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-z-1100 tw-backdrop-opacity-10">
    <div class="tw-py-10 tw-w-full tw-flex tw-flex-col">
      <div class="
          tw-bg-connx-white
          tw-rounded-md
          tw-mx-auto
          tw-border
          tw-border-solid
          tw-flex-1
          tw-w-full
          tw-max-w-[760px]
          tw-shadow-sm
          tw-flex
        ">
        <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
          <header
            class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-8 tw-flex tw-items-center tw-justify-between"
          >
            <h1 class="tw-text-2xl tw-font-semibold tw-text-connx-secondary-header tw-mb-0">
              Change Employee Code
            </h1>
            <button
              type="button"
              class="tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
              @click="emit('close')"
            >
              <connx-icon
                name="fal fa-times"
                class="tw-w-3"
              />
            </button>
          </header>
          <main class="
              tw-mx-auto
              tw-w-full
              tw-max-w-5xl
              tw-h-full
              tw-flex
              tw-flex-col
              tw-pt-10
              tw-justify-start
            ">
            <connx-progress-steps
              :current-step="currentStep"
              :steps="steps"
              class="tw-pb-4 tw-shadow"
            />
            <div class="tw-pt-10 tw-px-8 ScrollArea">
              <section>
                <div class="tw-flex tw-flex-wrap tw-justify-center">
                  <div class="tw-inline-grid tw-grid-cols-2 tw-gap-y-4 tw-gap-x-10 mx-auto">
                    <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                      Selected Employee:
                    </h6>
                    <div>{{ employee?.givenName }} {{ employee?.surname }}</div>
                    <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                      {{ completed ? 'Old' : 'Current' }} Employee Code:
                    </h6>
                    <div>{{ employee?.empCode }}</div>
                    <h6 class="tw-leading-normal tw-text-connx-grey-shade-20 tw-flex tw-items-center tw-mb-0">
                      New Employee Code:
                    </h6>
                    <connx-form v-if="currentStep === 1">
                      <connx-field
                        v-model="newEmpCode"
                        autofocus
                        :type="InputType.Text"
                        name="new-emp-code"
                        :maxlength="10"
                        class="tw-mb-0 tw-w-[140px]"
                        placeholder="Please enter code"
                      >
                      </connx-field>
                    </connx-form>

                    <div v-else>
                      {{ newEmpCode }}
                    </div>
                  </div>
                  <connx-alert
                    v-if="empCodeExists"
                    class="tw-mt-10 tw-flex-nowrap"
                    type="error"
                  >
                    The new employee code you entered already exists. Please enter another code.
                  </connx-alert>
                  <div
                    v-if="currentStep === 2"
                    class="tw-mt-10 tw-flex tw-flex-wrap tw-justify-center"
                  >
                    <connx-alert
                      type="warning"
                      class="tw-mb-4 tw-flex tw-flex-nowrap tw-items-center"
                    >
                      <div>
                        <p class="tw-text-connx-white tw-mb-3">
                          If you continue with the process, records for the employee will be changed in <br>
                          all places where they exist.
                        </p>
                        <p class="tw-text-connx-white tw-mb-3">
                          <strong class="tw-text-connx-white">There is no way to roll them back</strong> if you
                          accidentally select the wrong employee code.
                        </p>
                        <p class="tw-text-connx-white tw-mb-3">
                          The employee system password will no longer work. You need to reset the employee's password.
                        </p>
                        <p class="tw-text-connx-white tw-mb-0">
                          If the employee is a report user, report access will be lost. You need to add the employee as
                          a report user.
                        </p>
                      </div>
                    </connx-alert>
                  </div>
                  <div
                    v-if="currentStep === 3"
                    class="tw-mt-10 tw-w-full tw-text-center"
                  >
                    <div v-if="errorMessage">
                      <connx-alert type="error">
                        <div>
                          <h6 class="tw-text-connx-white tw-mb-3">
                            The employee code of {{ employee?.givenName }} {{ employee?.surname }} ({{ employee?.empCode
                            }}) was not changed.
                          </h6>
                          Error: {{ errorMessage }}
                        </div>
                      </connx-alert>
                    </div>
                    <connx-alert
                      v-else
                      type="success"
                    >
                      The employee code of
                      {{ employee?.givenName }} {{ employee?.surname }} has been updated.
                    </connx-alert>
                  </div>
                </div>
              </section>
            </div>
          </main>
          <footer
            class="tw-rounded-b-lg tw-w-full tw-bg-connx-grey-tint-85 tw-mx-auto tw-border-solid tw-border-t tw-border-connx-grey-tint-80 tw-py-6"
          >
            <div class="tw-px-8">
              <div
                class="tw-flex tw-flex-wrap tw-items-start"
                :class="{
                  'tw-justify-center': currentStep !== 2,
                  'tw-justify-between': currentStep === 2,
                }"
              >
                <connx-field
                  v-model="confirmEmployeeChange"
                  v-if="currentStep === 2"
                  name="confirm-emp-change"
                  unpadded
                  class="tw-w-full md:tw-w-auto tw-p-0 tw-py-4 md:tw-py-0 md:tw-pr-4 tw-mb-0"
                  :type="InputType.Checkbox"
                >
                  <div>
                    <p class="tw-mb-1">
                      I understand changes are permanent and there is no roll back.
                    </p>
                    <p class="tw-mb-0">
                      I still would like to continue with the process.
                    </p>
                  </div>
                </connx-field>
                <div>
                  <connx-button
                    v-if="currentStep > 1 && !completed"
                    icon="fas fa-arrow-left"
                    icon-position="before"
                    aria-label="Previous"
                    class="tw-bg-connx-white tw-w-full md:tw-w-36 md:tw-mr-4 tw-mb-4 md:tw-mb-0"
                    @click="decrementStep()"
                  >
                    Previous
                  </connx-button>
                  <connx-button
                    v-if="!completed"
                    :icon="currentStep === 1 ? 'fas fa-arrow-right' : 'far fa-shield-check'"
                    :icon-position="currentStep === 1 ? 'after' : 'before'"
                    button-style="success"
                    aria-label="Next"
                    :disabled="nextButtonDisabled"
                    @click="validateAndIncrementStep()"
                  >
                    {{ currentStep === 1 ? 'Next' : 'Complete' }}
                  </connx-button>
                  <connx-button
                    v-if="completed"
                    @click="emit('close')"
                    class="tw-bg-connx-white"
                  >
                    Close
                  </connx-button>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>
